import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { FetchAPI, FieldAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnClick, OnDoubleClick, OnDynamicContentRender, OnMount, OnSelectItem, OnUnselectItem } from '@philips-emr/tws-core/lib/client';
import { SubmittableFragmentContext } from '@philips-emr/tws-core/lib/component/context';
import { MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services';

import BaseController from '../BaseController';

import './locator-beneficiarios.scss';

enum IeStatusBeneficiario {
  MIGRACAO_ABERTA = 'M',
  ATIVO = 'A',
  INATIVO = 'I',
  ADESAO_ABERTA = 'D'
}

interface BeneficiarioSelecionado {
  nrSeqSegurado: number;
  dsTitularidade: string;
  ieSituacao: IeStatusBeneficiario;
}

type DataValue = Record<string, Value>;

@Controller({ fragment: 'locator-beneficiarios' })
class LocatorBeneficiariosController extends BaseController {

  @Inject('SubmittableFragmentContext')
  private submittableFragmentContext: SubmittableFragmentContext;

  private beneficiarioSelecionado: Record<string, Value> = {};

  @OnMount({ target: 'field-titular' })
  onMountFieldTitular(event: MountEvent): void {
    const field = event.target as unknown as FieldAPI;
    const value = field.getData() as unknown as BeneficiarioSelecionado;
    value && value.nrSeqSegurado && field.setLabel(value.dsTitularidade);
  }

  @OnSelectItem({ target: 'deck-beneficiarios' })
  onSelectItemBeneficiarios(event: SelectItemEvent<DataValue>): void {
    this.beneficiarioSelecionado = event.item;
  }

  @OnUnselectItem({ target: 'deck-beneficiarios' })
  onUnselectItemBeneficiarios(): void {
    this.beneficiarioSelecionado = {};
  }

  @OnBeforeFetch({ target: 'fetch-beneficiarios' })
  onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;

    const ids = this.context.get('beneficiarios') as number[] || [];
    const { nrSeqSubestipulante } =  StipulatorContext.getCurrentContract();
    const beneficiarios = { beneficiarios: ids.join(',') };
    const isDossie = this.context.get('isDossie');
    fetch.setParameters({ ...beneficiarios, ...fetch.getParameters(), ...(nrSeqSubestipulante && { nrSeqSubestipulante }),  isDossie });
  }

  @OnClick({ target: 'button-confirmar' })
  onClickButtonConfirmar(): void {
    this.submittableFragmentContext.submit(new SubmitEvent({ record: this.beneficiarioSelecionado }));
  }

  @OnDoubleClick({ target: 'hpms-custom-card-beneficiarios' })
  onClickDeck(): void {
    this.submittableFragmentContext.submit(new SubmitEvent({ record: this.beneficiarioSelecionado }));
  }

  @OnDynamicContentRender({ target: 'hpms-custom-card-beneficiarios' })
  onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieSituacao } = data as BeneficiarioSelecionado;

    const content = (): ReactElement => this.getBeneficiarioLoteStampByStatus(ieSituacao);
    render(content);
  }

  private getBeneficiarioLoteStampByStatus(ieSituacao: IeStatusBeneficiario): ReactElement {
    return (
      <Stamp
        className="c-locator-beneficiarios-exclusao--custom-stamp-margin"
        color={this.getColorSituacao(ieSituacao)}
        icon={this.getIconSituacao(ieSituacao)}
        label={this.getLabelSituacao(ieSituacao)}
      />
    );
  }

  private getLabelSituacao(ieSituacao: IeStatusBeneficiario): string {
    const label = {
      [IeStatusBeneficiario.ATIVO]: msg('$expression.$894905'),
      [IeStatusBeneficiario.INATIVO]: msg('$expression.$319239'),
      [IeStatusBeneficiario.ADESAO_ABERTA]: msg('$expression.$330512'),
      [IeStatusBeneficiario.MIGRACAO_ABERTA]: msg('$expression.$331187'),
    };

    return label[ieSituacao] && msg(label[ieSituacao]);
  }

  private getColorSituacao(ieSituacao: IeStatusBeneficiario): Color {
    const label = {
      [IeStatusBeneficiario.ATIVO]: Color.SAFETY_GREEN,
      [IeStatusBeneficiario.INATIVO]: Color.SAFETY_RED,
      [IeStatusBeneficiario.ADESAO_ABERTA]: Color.SAFETY_YELLOW,
      [IeStatusBeneficiario.MIGRACAO_ABERTA]: Color.SAFETY_BLUE,
    };

    return label[ieSituacao];
  }

  private getIconSituacao(ieSituacao: IeStatusBeneficiario): StampIcon {
    const icon = {
      [IeStatusBeneficiario.ATIVO]: StampIcon.CHECK,
      [IeStatusBeneficiario.INATIVO]: StampIcon.CANCELED,
      [IeStatusBeneficiario.ADESAO_ABERTA]: StampIcon.CLOCK,
      [IeStatusBeneficiario.MIGRACAO_ABERTA]: StampIcon.CLOCK,

    };

    return icon[ieSituacao];
  }

}

export default LocatorBeneficiariosController;
